import { useCallback, useEffect, useState } from "react";
import api from "@/lib/crmApi";
import { KpiCard } from "@/crm/ui";
import { PhoneCall, PhoneMissed, Timer, Percent } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis,
  Tooltip, CartesianGrid, Legend,
} from "recharts";

const daysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

export default function CallStats() {
  const [start, setStart] = useState(daysAgo(30));
  const [end, setEnd] = useState(daysAgo(0));
  const [commercial, setCommercial] = useState("");
  const [commercials, setCommercials] = useState([]);
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/users/commercials").then(({ data }) => setCommercials(data)).catch(() => {}); }, []);

  const load = useCallback(() => {
    const params = { start_date: start, end_date: end };
    if (commercial) params.commercial_id = commercial;
    api.get("/reports/call-stats", { params }).then(({ data }) => setData(data)).catch(() => {});
  }, [start, end, commercial]);

  useEffect(() => { load(); }, [load]);

  const t = data?.totals;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 mb-4" data-testid="call-stats-section">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="font-heading font-semibold text-slate-800 flex items-center gap-2"><PhoneCall size={17} /> Rapports téléphoniques</h3>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <input data-testid="callstats-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="px-2 py-1.5 rounded-lg border border-slate-300" />
          <span className="text-slate-400">→</span>
          <input data-testid="callstats-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="px-2 py-1.5 rounded-lg border border-slate-300" />
          <select data-testid="callstats-commercial" value={commercial} onChange={(e) => setCommercial(e.target.value)} className="px-2 py-1.5 rounded-lg border border-slate-300 bg-white">
            <option value="">Tous les commerciaux</option>
            {commercials.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      </div>

      {!data ? <p className="text-sm text-slate-400 py-6 text-center">Chargement…</p> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
            <KpiCard label="Appels sur la période" value={t.total_calls} icon={PhoneCall} accent="indigo" />
            <KpiCard label="Taux de réponse" value={`${t.answer_rate}%`} icon={Percent} accent="emerald" />
            <KpiCard label="Taux d'appels perdus" value={`${t.missed_rate}%`} icon={PhoneMissed} accent="rose" />
            <KpiCard label="Durée moyenne (s)" value={t.avg_duration} icon={Timer} accent="sky" />
          </div>

          {data.per_commercial.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
              <div>
                <h4 className="text-sm font-medium text-slate-600 mb-2">Comparatif par commercial</h4>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={data.per_commercial}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                    <XAxis dataKey="commercial_name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip /><Legend />
                    <Bar dataKey="answered" name="Répondus" fill="#059669" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="missed" name="Perdus" fill="#e11d48" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div>
                <h4 className="text-sm font-medium text-slate-600 mb-2">Évolution des appels</h4>
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={data.daily}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip /><Legend />
                    <Line type="monotone" dataKey="total" name="Total" stroke="#4f46e5" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="answered" name="Répondus" stroke="#059669" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="missed" name="Perdus" stroke="#e11d48" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {data.per_commercial.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-4">Aucun appel sur la période sélectionnée</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 text-xs uppercase">
                <tr><th className="p-2 text-left">Commercial</th><th className="p-2 text-right">Appels</th><th className="p-2 text-right">Répondus</th><th className="p-2 text-right">Perdus</th><th className="p-2 text-right">Taux réponse</th><th className="p-2 text-right">Durée moy. (s)</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.per_commercial.map((r) => (
                  <tr key={r.commercial_id || "none"} data-testid={`callstats-row-${r.commercial_id}`}>
                    <td className="p-2 font-medium text-slate-800">{r.commercial_name}</td>
                    <td className="p-2 text-right font-mono">{r.total_calls}</td>
                    <td className="p-2 text-right font-mono text-emerald-600">{r.answered}</td>
                    <td className="p-2 text-right font-mono text-rose-600">{r.missed}</td>
                    <td className="p-2 text-right font-mono">{r.answer_rate}%</td>
                    <td className="p-2 text-right font-mono">{r.avg_duration}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}
